import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Star } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/18435659/pexels-photo-18435659.jpeg";
const HERO_SECONDARY = "https://images.pexels.com/photos/18239440/pexels-photo-18239440.jpeg";
const BESPOKE_IMG = "https://images.pexels.com/photos/27174573/pexels-photo-27174573.jpeg";

const COLLECTIONS = [
  { key: "clothing", title: "The Atelier Wardrobe", sub: "Tailored modesty", img: "https://images.pexels.com/photos/5120186/pexels-photo-5120186.jpeg" },
  { key: "hijabs", title: "Hijab Edit", sub: "Silk · Modal · Chiffon", img: "https://images.pexels.com/photos/18239440/pexels-photo-18239440.jpeg" },
  { key: "bags", title: "Bespoke Bags", sub: "Made-to-order", img: "https://images.pexels.com/photos/27174573/pexels-photo-27174573.jpeg" },
  { key: "accessories", title: "Accents", sub: "Final flourishes", img: "https://images.unsplash.com/photo-1611591437281-460bfbe1220a" },
];

const REVIEWS = [
  { name: "Aaliyah K.", city: "London", text: "The cashmere tunic is the most considered piece I own. It travels, drapes, lasts. Atelier Noir is the real thing.", rating: 5 },
  { name: "Sofia B.", city: "Dubai", text: "My bespoke bag arrived in a hand-stitched dust pouch. The garnet leather is exactly what I sketched.", rating: 5 },
  { name: "Nour A.", city: "Paris", text: "Their silk hijabs feel like air. I have replaced my entire collection with theirs.", rating: 5 },
];

const INSTA = [
  "https://images.pexels.com/photos/18435659/pexels-photo-18435659.jpeg",
  "https://images.pexels.com/photos/13562820/pexels-photo-13562820.jpeg",
  "https://images.unsplash.com/photo-1614179689702-355944cd0918",
  "https://images.pexels.com/photos/5120186/pexels-photo-5120186.jpeg",
  "https://images.unsplash.com/photo-1584917865442-de89df76afd3",
  "https://images.pexels.com/photos/18239440/pexels-photo-18239440.jpeg",
];

export default function Home() {
  const [best, setBest] = useState([]);
  const [nw, setNw] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { bestseller: true } }).then((r) => setBest(r.data.slice(0, 4)));
    api.get("/products", { params: { is_new: true } }).then((r) => setNw(r.data.slice(0, 4)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative">
        <div className="grid lg:grid-cols-12 max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 pb-20 gap-10 items-end">
          <div className="lg:col-span-6 fade-up">
            <div className="overline text-[#C5A059]">— Spring Edition · 2026</div>
            <h1 className="serif text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] mt-4">
              Elegance<br/>designed<br/>for every<br/><em className="text-[#C5A059] not-italic">woman.</em>
            </h1>
            <p className="text-[#5A5A5A] max-w-md mt-8 leading-relaxed">
              Discover modest fashion, premium hijabs, and handcrafted custom bags — each piece made with quiet intention, for the way you actually live.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/shop" data-testid="hero-shop-now" className="btn-primary">Shop now <ArrowRight size={14} /></Link>
              <Link to="/custom-bag" data-testid="hero-custom-order" className="btn-outline">Bespoke a bag</Link>
            </div>
          </div>
          <div className="lg:col-span-6 relative fade-up delay-200">
            <div className="img-zoom aspect-[4/5] overflow-hidden">
              <img src={HERO_IMG} alt="Modest fashion editorial" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden lg:block w-40 aspect-[3/4] img-zoom border-4 border-[#F5F5F0]">
              <img src={HERO_SECONDARY} alt="Detail" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-y border-[#E5E5DF] py-5 marquee bg-[#EFEFE8] overflow-hidden">
          <div className="marquee-track">
            {[1, 2].map((k) => (
              <div key={k} className="flex gap-10 text-[0.75rem] tracking-[0.3em] uppercase text-[#5A5A5A] items-center">
                <span>Bespoke craftsmanship</span><span className="text-[#C5A059]">✦</span>
                <span>Modest elegance</span><span className="text-[#C5A059]">✦</span>
                <span>Premium silks</span><span className="text-[#C5A059]">✦</span>
                <span>Hand-finished leather</span><span className="text-[#C5A059]">✦</span>
                <span>Made for life</span><span className="text-[#C5A059]">✦</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Collections — Tetris */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline">Curated Collections</div>
            <h2 className="serif text-4xl lg:text-5xl mt-3">Explore the maison</h2>
          </div>
          <Link to="/shop" className="hidden lg:inline-flex btn-ghost">View all →</Link>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 lg:grid-rows-2 gap-4 lg:h-[640px]">
          <Link to="/shop/clothing" data-testid="collection-clothing" className="img-zoom lg:col-span-2 lg:row-span-2 relative bg-black h-80 lg:h-full">
            <img src={COLLECTIONS[0].img} alt={COLLECTIONS[0].title} className="w-full h-full object-cover opacity-90" />
            <div className="absolute bottom-8 left-8 text-white">
              <div className="text-[0.65rem] tracking-[0.3em] uppercase opacity-80">{COLLECTIONS[0].sub}</div>
              <div className="serif text-3xl lg:text-5xl mt-2">{COLLECTIONS[0].title}</div>
            </div>
          </Link>
          {COLLECTIONS.slice(1).map((c) => (
            <Link to={`/shop/${c.key}`} data-testid={`collection-${c.key}`} key={c.key} className="img-zoom relative bg-black h-64 lg:h-full">
              <img src={c.img} alt={c.title} className="w-full h-full object-cover opacity-90" />
              <div className="absolute bottom-5 left-5 text-white">
                <div className="text-[0.6rem] tracking-[0.3em] uppercase opacity-80">{c.sub}</div>
                <div className="serif text-2xl lg:text-3xl mt-1">{c.title}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Best Sellers */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline">Best Sellers</div>
            <h2 className="serif text-4xl lg:text-5xl mt-3">Pieces she returned for.</h2>
          </div>
          <Link to="/shop?sort=popular" className="btn-ghost hidden lg:inline-flex">All bestsellers →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12">
          {best.map((p, i) => <ProductCard key={p.id} product={p} idx={i} />)}
        </div>
      </section>

      {/* Bespoke band */}
      <section className="bg-[#0A0A0A] text-white my-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="img-zoom aspect-[4/5] overflow-hidden">
            <img src={BESPOKE_IMG} alt="Bespoke bag in cognac leather" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="overline text-[#C5A059]">— Bespoke Atelier</div>
            <h2 className="serif text-4xl lg:text-6xl leading-tight mt-4">Your bag,<br/> as you imagined it.</h2>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              Choose silhouette, leather, lining, and hardware. Add your initials. Upload a sketch. We hand-cut, hand-stitch, and ship within four weeks.
            </p>
            <Link to="/custom-bag" data-testid="bespoke-cta" className="inline-flex mt-10 items-center gap-3 text-[0.75rem] tracking-[0.25em] uppercase border-b border-[#C5A059] pb-2 hover:text-[#C5A059] transition-colors">
              Start your design <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* New Arrivals */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline">New Arrivals</div>
            <h2 className="serif text-4xl lg:text-5xl mt-3">Just into the atelier.</h2>
          </div>
          <Link to="/shop?sort=newest" className="btn-ghost hidden lg:inline-flex">All new →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12">
          {nw.map((p, i) => <ProductCard key={p.id} product={p} idx={i} />)}
        </div>
      </section>

      {/* Reviews */}
      <section className="bg-[#EFEFE8] my-24 py-24 px-6 lg:px-12">
        <div className="max-w-[1100px] mx-auto text-center">
          <div className="overline">Voices</div>
          <h2 className="serif text-4xl lg:text-5xl mt-3">From the women who wear us.</h2>
          <div className="grid lg:grid-cols-3 gap-10 mt-16 text-left">
            {REVIEWS.map((r) => (
              <div key={r.name} className="border-l border-[#C5A059] pl-6">
                <div className="flex gap-1 text-[#C5A059] mb-4">{Array.from({length:r.rating}).map((_,i) => <Star key={i} size={13} fill="#C5A059" strokeWidth={0} />)}</div>
                <p className="serif text-xl leading-snug">"{r.text}"</p>
                <div className="overline mt-5">{r.name} · {r.city}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
        <div className="text-center mb-10">
          <a href="https://instagram.com/mohwaheeb" target="_blank" rel="noopener noreferrer" data-testid="home-instagram-link" className="overline hover:text-[#C5A059] transition-colors">@mohwaheeb</a>
          <h2 className="serif text-4xl lg:text-5xl mt-3">Inside the studio.</h2>
        </div>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
          {INSTA.map((src, i) => (
            <a key={i} href="https://instagram.com/mohwaheeb" target="_blank" rel="noopener noreferrer" data-testid={`insta-tile-${i}`} className="img-zoom aspect-square-img block">
              <img src={src} alt="" className="w-full h-full object-cover" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
